/* Dollar Labs B2B — Paintlab theme integration.
 *
 * Renders price-list pricing in place of the theme's own prices on product
 * pages, collection/search cards, and the cart, and adds the "next volume tier"
 * prompt to cart lines.
 *
 * All pricing comes from the SDK the Dollar Labs app embed installs
 * (DollarlabsGetBestPrice / DollarlabsGetNextTierWarning), so there is no second
 * copy of the pricing rules here. Liquid renders a hidden [data-v3] seed node per
 * variant (snippets/dollarlabs-v3-seed.liquid), which the SDK's cache reads
 * synchronously — on-page variants resolve with no Storefront API round-trip.
 *
 * Nothing here touches the theme's own rendering unless the price list actually
 * produces a discount for this customer. A logged-out retail shopper, or a
 * customer whose price list matches the shelf price, sees the theme exactly as
 * it is today — including native compare-at sales.
 */
(function () {
  "use strict";

  var CONFIG_ID = "dl-b2b-config";
  var BEST_PRICE = "DollarlabsGetBestPrice";
  var NEXT_TIER = "DollarlabsGetNextTierWarning";
  var PRICING_STRING = "DollarlabsGetPricingString";

  var cfg = readConfig();
  if (!cfg || !cfg.enabled) return;

  /* ─────────────────────────── config & utilities ─────────────────────────── */

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // The app embed's script is deferred and may not have run yet on first paint.
  function waitForApi() {
    if (typeof window[BEST_PRICE] === "function") return Promise.resolve(true);
    return new Promise(function (resolve) {
      var waited = 0;
      var tick = setInterval(function () {
        if (typeof window[BEST_PRICE] === "function") {
          clearInterval(tick);
          resolve(true);
        } else if ((waited += 100) >= 8000) {
          clearInterval(tick);
          resolve(false);
        }
      }, 100);
    });
  }

  function withDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return "0";
    var parts = (cents / 100.0).toFixed(precision).split(".");
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    return whole + (parts[1] ? decimal + parts[1] : "");
  }

  // Mirrors the app's own money formatter so our derived figures (ex-VAT lines,
  // struck originals) format identically to the SDK's formattedPrice.
  function money(cents) {
    var fmt = cfg.moneyFormat;
    if (!fmt) return (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case "amount":
          return withDelimiters(cents, 2, ",", ".");
        case "amount_no_decimals":
          return withDelimiters(cents, 0, ",", ".");
        case "amount_with_comma_separator":
          return withDelimiters(cents, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return withDelimiters(cents, 0, ".", ",");
        case "amount_with_apostrophe_separator":
          return withDelimiters(cents, 2, "'", ".");
        case "amount_no_decimals_with_space_separator":
          return withDelimiters(cents, 0, " ", ".");
        case "amount_with_space_separator":
          return withDelimiters(cents, 2, " ", ",");
        case "amount_with_period_and_space_separator":
          return withDelimiters(cents, 2, " ", ".");
        default:
          return match;
      }
    });
  }

  // The theme's displayed prices are VAT-inclusive; the "exc. VAT" line is a
  // display-only derivation, so it must be taken off whatever we end up showing.
  function exVat(cents) {
    return Math.round(cents / (1 + cfg.vatRate));
  }

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  // Writes that no-op when the content already matches, so our own mutations
  // settle instead of re-triggering the observers that watch these nodes.
  function setHtml(el, html) {
    if (!el || el.innerHTML === html) return false;
    el.innerHTML = html;
    return true;
  }

  // Settings text is merchant-controlled, but it still goes through innerHTML.
  function esc(text) {
    return String(text == null ? "" : text).replace(/[&<>"]/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch];
    });
  }

  function debounce(fn, ms) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }

  /* ───────────────────────────── pricing lookups ──────────────────────────── */

  // Resolved price for one variant, or null when the price list has nothing to
  // say about it. `originalPrice === 0` is the SDK's "no V3 data at all" shape,
  // which must not be mistaken for a free product.
  function bestPrice(variantId, quantity) {
    if (!variantId || typeof window[BEST_PRICE] !== "function") {
      return Promise.resolve(null);
    }
    return Promise.resolve(window[BEST_PRICE](variantId, quantity || 1))
      .then(function (res) {
        if (!res || (!res.hasDiscount && !res.originalPrice)) return null;
        return res;
      })
      .catch(function () {
        return null;
      });
  }

  // Which price-list columns actually apply to this customer for this variant.
  // The SDK returns only applicable columns, each flagged isCustomerTag /
  // isDefault — which is the whole member-vs-public distinction, decided by the
  // app's own tag rules rather than a guess from `customer != blank`.
  function pricingColumns(variantId) {
    if (typeof window[PRICING_STRING] !== "function") return Promise.resolve(null);
    return Promise.resolve(window[PRICING_STRING](variantId)).catch(function () {
      return null;
    });
  }

  function nextTier(variantId, quantity) {
    if (!variantId || !quantity || typeof window[NEXT_TIER] !== "function") {
      return Promise.resolve(null);
    }
    return Promise.resolve(window[NEXT_TIER](variantId, quantity)).catch(
      function () {
        return null;
      },
    );
  }

  /* ─────────────────── product page: the pe-* Paintlab layouts ────────────── */
  /* Shared by paintlab-default-new, paintlab-emulsion-product and
   * product-new-design-V2 — all three render the same #pe-price / #pe-price-excl
   * / #pe-price-compare trio and the same #pe-variants-json payload. Each
   * section's own JS rewrites those nodes on every variant change, so we watch
   * the price block and re-apply rather than trying to hook five call sites. */

  var peVariants = null;

  function peVariantsById(root) {
    if (peVariants) return peVariants;
    peVariants = {};
    var json = document.getElementById("pe-variants-json");
    if (!json) return peVariants;
    try {
      JSON.parse(json.textContent || "[]").forEach(function (v) {
        peVariants[String(v.id)] = v;
      });
    } catch (e) {
      /* leave the map empty; every lookup then falls back to the theme price */
    }
    return peVariants;
  }

  function peQuantity(root) {
    if (!cfg.priceFollowsQuantity) return 1;
    var input = root.querySelector("#pe-qty-input");
    var qty = toInt(input && input.value, 1);
    return qty > 0 ? qty : 1;
  }

  function peRender(root) {
    var input = root.querySelector("#pe-variant-id");
    var variantId = input && input.value;
    if (!variantId) return Promise.resolve();

    var variant = peVariantsById(root)[String(variantId)];
    if (!variant) return Promise.resolve();

    var base = toInt(variant.price, 0);
    var vatRate = parseFloat(root.getAttribute("data-dl-vat-rate"));
    var quantity = peQuantity(root);

    return bestPrice(variantId, quantity).then(function (res) {
      if (!res) return; // no price list for this variant — theme rendering stands
      var shown = res.hasDiscount ? res.priceInCents : base;

      setHtml(root.querySelector("#pe-price"), money(shown));

      var excl = root.querySelector("#pe-price-excl");
      if (excl) {
        var net = isNaN(vatRate)
          ? exVat(shown)
          : Math.round(shown / (1 + vatRate));
        setHtml(excl, money(net) + " " + cfg.exVatSuffix);
      }

      // Only claim the compare slot when we have a price-list discount to show.
      // Otherwise the theme's own compare-at handling (native sale) is left alone.
      var compare = root.querySelector("#pe-price-compare");
      if (compare && res.hasDiscount) {
        setHtml(compare, money(base));
        compare.style.display = "";
      }
    });
  }

  // Prices printed inside the size buttons ("2.7L / €48.00"). Each button carries
  // the variant Liquid resolved for that option value, so no re-derivation here.
  function peRenderOptionPrices(root) {
    var buttons = root.querySelectorAll("[data-dl-variant-id] .pe-sz-price");
    return Promise.all(
      Array.prototype.map.call(buttons, function (el) {
        var button = el.closest("[data-dl-variant-id]");
        var variantId = button.getAttribute("data-dl-variant-id");
        var base = toInt(button.getAttribute("data-dl-base-price"), 0);
        return bestPrice(variantId, 1).then(function (res) {
          if (!res) return;
          if (!res.hasDiscount) return setHtml(el, money(base));
          setHtml(
            el,
            '<s class="dl-was">' +
              money(base) +
              "</s> " +
              money(res.priceInCents),
          );
        });
      }),
    );
  }

  function initPeProduct() {
    var root = document.querySelector("[data-dl-pe]");
    if (!root) return;

    var run = function () {
      peRender(root);
      peRenderOptionPrices(root);
    };

    run();

    // paintlab-default-new announces variant changes; the other two do not, so
    // the observer below is what actually carries them.
    root.addEventListener("variant:change", run);

    ["click", "change", "input"].forEach(function (evt) {
      root.addEventListener(evt, function () {
        setTimeout(run, 0); // let the section's own handler write first
      });
    });

    var block = root.querySelector(".pe-price-block");
    if (block) {
      new MutationObserver(debounce(run, 40)).observe(block, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  /* ──────────── product page: the older Ella layouts (surfup / osma) ───────── */
  /* main-product and osma-product update prices from jQuery selectCallback,
   * writing .compare-price / .vat__product / .ex__product and keeping the base
   * price in #product_regular_price. */

  function ellaVariantId(scope) {
    var input = scope.querySelector('[name="id"]');
    if (input && input.value) return input.value;
    var match = window.location.search.match(/[?&]variant=(\d+)/);
    return match ? match[1] : null;
  }

  function ellaRender(prices) {
    var scope = document.querySelector(".product") || document;
    var variantId = ellaVariantId(scope);
    var baseInput = document.getElementById("product_regular_price");
    var base = toInt(baseInput && baseInput.value, 0);
    if (!variantId || !base) return Promise.resolve();

    return bestPrice(variantId, 1).then(function (res) {
      if (!res) return;
      var shown = res.hasDiscount ? res.priceInCents : base;

      // The unit-price block also carries .vat__product; exclude it.
      var incl = prices.querySelector(
        ".vat__product:not(.vat__product__unit):not(.compare-price)",
      );
      if (incl) {
        setHtml(
          incl,
          money(shown) +
            ' <span class="ex__product--text">' +
            cfg.inclVatSuffix +
            "</span>",
        );
      }

      var excl = prices.querySelector(".ex__product");
      if (excl) {
        setHtml(
          excl,
          money(exVat(shown)) +
            ' <span class="ex__product--text">' +
            cfg.exVatSuffix +
            "</span>",
        );
      }

      if (!res.hasDiscount) return;
      var compare = prices.querySelector(".compare-price");
      if (!compare) {
        compare = document.createElement("span");
        compare.className = "compare-price";
        prices.insertBefore(compare, prices.firstChild);
      }
      setHtml(compare, money(base));
      compare.style.display = "";
    });
  }

  function initEllaProduct() {
    var prices = document.querySelector(".product .prices");
    if (!prices || document.querySelector("[data-dl-pe]")) return;

    var run = function () {
      ellaRender(prices);
    };
    run();

    new MutationObserver(debounce(run, 40)).observe(prices, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    var form = prices.closest("form") || document;
    form.addEventListener("change", function () {
      setTimeout(run, 0);
    });
  }

  /* ───────────────────── collection / search product cards ────────────────── */

  // Rebuild the card's price box in the theme's own "on sale" shape: struck
  // variant price, then our price. Keeping the theme's class names matters —
  // the tax selector toggles .vat__product / .ex__product visibility, and the
  // theme's stylesheet hangs its price typography off .special-price.
  // Member card block: accent rule, "Your trade price" eyebrow, the price, then
  // the RRP struck with the cash saving. Own class names rather than the theme's
  // — cards render incl-VAT only (the .ex__product spans are commented out in
  // the card snippet), so there is nothing here for the tax selector to toggle.
  function cardMemberMarkup(base, final) {
    var save = base - final;
    var html =
      '<div class="dl-price">' +
      '<span class="dl-price__eyebrow">' +
      esc(cfg.member.label) +
      "</span>" +
      '<span class="dl-price__now">' +
      money(final) +
      " " +
      esc(cfg.inclVatSuffix) +
      "</span>";

    if (save > 0) {
      html +=
        '<span class="dl-price__meta">' +
        esc(cfg.member.rrpLabel) +
        ' <s class="dl-price__rrp">' +
        money(base) +
        "</s> &middot; " +
        esc(cfg.member.saveLabel) +
        " " +
        money(save) +
        "</span>";
    }
    return html + "</div>";
  }

  function cardSaleMarkup(layout, base, final) {
    if (layout === "list") {
      return (
        '<div class="price-sale">' +
        '<span class="old-price">' +
        money(base) +
        "</span>" +
        '<span class="special-price">' +
        money(final) +
        "</span>" +
        "</div>"
      );
    }
    return (
      '<div class="price-sale"><div class="special-price">' +
      '<span class="dual-pricing">' +
      '<span class="vat__product compare-price">' +
      money(base) +
      "</span>" +
      '<span class="vat__product">' +
      money(final) +
      " " +
      cfg.inclVatSuffix +
      "</span>" +
      "</span></div></div>"
    );
  }

  // "<id>:<cents>,<id>:<cents>" — every available variant, in the product's own
  // variant order, as Liquid wrote it.
  function cardCandidates(box) {
    return (box.getAttribute("data-dl-variants") || "")
      .split(",")
      .map(function (pair) {
        var parts = pair.split(":");
        return { id: parts[0], base: toInt(parts[1], 0) };
      })
      .filter(function (c) {
        return c.id && c.base;
      });
  }

  // Which variant should the card price?
  //
  // The theme answers "the first available one with a compare-at sale", so a
  // card can carry a badge. On multi-base paint that rule lands on an internal
  // tint variant (9L/C) the shopper can never deliberately select, and whose
  // price-list percentage differs from the base variant's — so the card quoted a
  // price nobody could reach. Re-read against the price list instead: the first
  // available variant genuinely discounted for THIS customer. When none is, the
  // card is left exactly as the theme rendered it, compare-at sale and all.
  function pickCardVariant(candidates) {
    return Promise.all(
      candidates.map(function (c) {
        return bestPrice(c.id, 1).then(function (res) {
          // sawData distinguishes "this variant is on a price list but not
          // discounted for you" from "this variant has no price list at all".
          if (!res) return { sawData: false, hit: null };
          if (!res.hasDiscount) return { sawData: true, hit: null };
          return pricingColumns(c.id).then(function (cols) {
            // A tag column applying means this price belongs to *this* customer,
            // so it can be labelled as theirs. A __default__-only column is a
            // price everyone gets, and is shown as an ordinary sale.
            var member = !!(
              cols &&
              cols.pricings &&
              cols.pricings.some(function (col) {
                return col.isCustomerTag;
              })
            );
            return {
              sawData: true,
              hit: {
                base: res.originalPrice || c.base,
                final: res.priceInCents,
                member: member,
              },
            };
          });
        });
      }),
    ).then(function (results) {
      var out = { sawData: false, hit: null };
      for (var i = 0; i < results.length; i++) {
        if (results[i].sawData) out.sawData = true;
        if (!out.hit && results[i].hit) out.hit = results[i].hit;
      }
      return out;
    });
  }

  var EMPTY_CARD = { sawData: false, hit: null };

  function renderCard(card) {
    var boxes = card.querySelectorAll("[data-dl-card-price]");
    if (!boxes.length) return Promise.resolve(EMPTY_CARD);

    var candidates = cardCandidates(boxes[0]);
    if (!candidates.length) return Promise.resolve(EMPTY_CARD);

    return pickCardVariant(candidates).then(function (found) {
      var hit = found.hit;
      if (!hit) return found;

      Array.prototype.forEach.call(boxes, function (box) {
        var layout = box.getAttribute("data-dl-card-price");
        setHtml(
          box,
          hit.member && cfg.member.enabled
            ? cardMemberMarkup(hit.base, hit.final)
            : cardSaleMarkup(layout, hit.base, hit.final),
        );
      });
      card.classList.add("on-sale");
      return found;
    });
  }

  // On a card priced from the price list, the % badge is not restated against
  // our numbers — it is suppressed. custom.js would otherwise recompute it from
  // whatever two prices sit in the DOM; setting the label "processed" is that
  // script's own guard, so it early-returns on this card through its load,
  // scroll and mutation passes and the badge stays gone.
  //
  // Its sold-out branch runs *before* that guard, so a sold-out card keeps its
  // "Sold out" label — only the sale label is hidden here.
  function suppressBadge(card) {
    var label = card.querySelector(".product-label");
    if (!label) return;

    var sale = label.querySelector(".sale-label");
    if (sale) sale.style.display = "none";
    if (!label.querySelector(".sold-out-label")) label.style.display = "none";
    label.dataset.processed = "true";
  }

  // "Sale" stands in for an unfilled Product Badge metafield whenever a discount
  // badge is showing, so a discounted card never reads as an unlabelled one.
  // Off by default: settings.dl_card_sale_fallback keeps the old behaviour.
  function applySaleFallback(card) {
    if (!cfg.saleFallback || !cfg.saleFallback.enabled) return;

    var container = card.querySelector(".custom-product-badge-container");
    if (!container) return;

    var label = card.querySelector(".product-label");
    var sale = label && label.querySelector(".sale-label");
    // The theme renders .sale-label empty and fills it in only when it has a
    // percentage to print, so "contains a digit" is the honest test for showing.
    var badgeShowing =
      !!sale &&
      label.style.display !== "none" &&
      sale.style.display !== "none" &&
      /\d/.test(sale.textContent || "");

    var ours = container.querySelector("[data-dl-sale-fallback]");
    var merchantBadge = container.querySelector(
      ".custom-product-badge:not([data-dl-sale-fallback])",
    );

    if (!badgeShowing || merchantBadge) {
      if (ours) ours.remove();
      return;
    }
    if (ours) return;

    var el = document.createElement("p");
    el.className = "custom-product-badge";
    el.setAttribute("data-dl-sale-fallback", "");
    el.textContent = cfg.saleFallback.text;
    container.appendChild(el);
  }

  // A bar rendered for a signed-in shopper starts hidden, because Liquid cannot
  // tell whether their tags match a price-list column. Reveal it only on positive
  // evidence: the page carried price-list data and none of it was theirs. When
  // the page has no price-list data at all we cannot tell a retail account from a
  // trade one, so it stays hidden — a missing banner beats telling a trade
  // customer to apply for the account they already hold.
  function settleTradeBar(sawData, sawMember) {
    var pending = document.querySelectorAll("[data-dl-trade-bar-pending]");
    if (!pending.length) return;

    Array.prototype.forEach.call(pending, function (bar) {
      if (sawMember) {
        bar.remove();
      } else if (sawData) {
        bar.removeAttribute("hidden");
        bar.removeAttribute("data-dl-trade-bar-pending");
      }
    });
  }

  function scanCards(root) {
    var cards = Array.prototype.slice.call(
      (root || document).querySelectorAll(".product-item"),
    );
    if (!cards.length) return Promise.resolve();

    return Promise.all(
      cards.map(function (card) {
        return renderCard(card).then(function (found) {
          if (found.hit) suppressBadge(card);
          return found;
        });
      }),
    ).then(function (results) {
      var sawData = false;
      var sawMember = false;
      results.forEach(function (r) {
        if (r.sawData) sawData = true;
        if (r.hit && r.hit.member) sawMember = true;
      });
      settleTradeBar(sawData, sawMember);

      // Read the badge state the theme has settled on, not the one before it.
      setTimeout(function () {
        cards.forEach(applySaleFallback);
      }, 0);
    });
  }

  function initCards() {
    if (!document.querySelector(".product-item")) return;

    var rescan = debounce(function () {
      scanCards(document);
    }, 200);

    scanCards(document);

    // Filters, infinite scroll and quick-view all swap grids wholesale.
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        if (target && target.closest && target.closest("[data-dl-card-price]")) {
          continue; // our own write
        }
        rescan();
        return;
      }
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener("shopify:section:load", rescan);
  }

  /* ──────────────────────────── cart: tier prompts ────────────────────────── */

  // One entry per cart surface this store can render a line item in. Every one
  // exposes a variant id and a quantity, which is all the tier lookup needs.
  var CART_SURFACES = [
    {
      // Spurit OCC3 slide cart — the drawer live on paintlab.ie.
      line: '[data-occ3="product-container"]',
      variantId: function (el) {
        return el.getAttribute("data-occ3-id");
      },
      quantity: function (el) {
        var input = el.querySelector('[data-occ3="quantity"]');
        return toInt(input && input.value, 0);
      },
      mount: function (el) {
        return el.querySelector(".spurit-occ3__price");
      },
    },
    {
      // The theme's own Ella/Halo drawer, server-rendered and AJAX-rerendered.
      line: "#dropdown-cart li.item, #sidebar-cart li.item",
      variantId: function (el) {
        return el.getAttribute("data-product-id");
      },
      quantity: function (el) {
        var input = el.querySelector("input.item-quantity");
        return toInt(input && input.value, 0);
      },
      mount: function (el) {
        return (
          el.querySelector(".cart-collateral") ||
          el.querySelector(".product-details")
        );
      },
    },
    {
      // The /cart page.
      line: "li.cart-product-item",
      variantId: function (el) {
        return el.getAttribute("data-item-id");
      },
      quantity: function (el) {
        var input = el.querySelector('input[name="updates[]"]');
        return toInt(input && input.value, 0);
      },
      mount: function (el) {
        return (
          el.querySelector(".cart--price.h-desktop") ||
          el.querySelector(".cart--price")
        );
      },
    },
  ];

  // Rising counter invalidates in-flight lookups: a quantity can change twice
  // before the first await resolves, and the older answer must not win.
  var cartGeneration = 0;

  function fillTemplate(template, warning) {
    return String(template)
      .replace(/\[qty\]/g, String(warning.itemsNeeded))
      .replace(/\[price\]/g, warning.nextTierPriceFormatted)
      .replace(/\[percent\]/g, String(warning.savingsPercentage) + "%")
      .replace(/\[tier\]/g, String(warning.nextTierQuantity));
  }

  function promptFor(mount) {
    var el = mount.querySelector("[data-dl-tier-prompt]");
    if (!el) {
      el = document.createElement("div");
      el.className = "dl-tier-prompt";
      el.setAttribute("data-dl-tier-prompt", "");
      el.setAttribute("aria-live", "polite");
      el.hidden = true;
      mount.appendChild(el);
    }
    return el;
  }

  function renderTierPrompt(el, warning) {
    var html =
      '<span class="dl-tier-prompt__text">' +
      fillTemplate(cfg.cartTiers.message, warning) +
      "</span>";

    // savingsPerUnit comes back in display currency, not cents.
    if (cfg.cartTiers.showSavings && warning.savingsPerUnit > 0) {
      var total = warning.savingsPerUnit * warning.nextTierQuantity;
      html +=
        '<span class="dl-tier-prompt__savings">' +
        fillTemplate(cfg.cartTiers.savingsLabel, warning).replace(
          /\[amount\]/g,
          money(Math.round(total * 100)),
        ) +
        "</span>";
    }

    setHtml(el, html);
    el.hidden = false;
  }

  function updateCartLine(surface, line, token) {
    var mount = surface.mount(line);
    if (!mount) return;

    var variantId = surface.variantId(line);
    var quantity = surface.quantity(line);
    var el = promptFor(mount);

    if (!variantId || quantity < 1) {
      el.hidden = true;
      return;
    }

    // A re-rendered cart line gets a fresh (signature-less) prompt node, so this
    // never goes stale across theme re-renders.
    var signature = variantId + ":" + quantity;
    if (el.dataset.dlSignature === signature) return;

    nextTier(variantId, quantity).then(function (warning) {
      if (token !== cartGeneration) return; // superseded by a newer pass
      el.dataset.dlSignature = signature;
      if (warning && warning.itemsNeeded > 0) renderTierPrompt(el, warning);
      else {
        el.hidden = true;
        setHtml(el, "");
      }
    });
  }

  function scanCart() {
    var token = ++cartGeneration;
    CART_SURFACES.forEach(function (surface) {
      document.querySelectorAll(surface.line).forEach(function (line) {
        updateCartLine(surface, line, token);
      });
    });
  }

  function initCart() {
    if (!cfg.cartTiers || !cfg.cartTiers.enabled) return;

    var rescan = debounce(scanCart, 150);
    scanCart();

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        // Ignore our own writes, or the observer feeds itself.
        if (
          target &&
          target.closest &&
          target.closest("[data-dl-tier-prompt]")
        ) {
          continue;
        }
        rescan();
        return;
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value", "data-quantity", "data-occ3-id"],
    });

    ["cart:updated", "cart:refresh", "shopify:section:load"].forEach(
      function (evt) {
        document.addEventListener(evt, rescan);
      },
    );
    ["change", "input"].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (e.target && e.target.matches && e.target.matches("input")) rescan();
      });
    });
  }

  /* ─────────────────────────────────── boot ───────────────────────────────── */

  function start() {
    waitForApi().then(function (ready) {
      if (!ready) return; // app embed missing or disabled — theme stands as-is
      initPeProduct();
      initEllaProduct();
      initCards();
      initCart();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
